import { useRouter } from "@app/app/components/router/RouterContext";
import { useAppState, useCustomNavigate, usePropertyContext } from "@app/app/hooks";
import { getRouteChildValue } from "@app/app/utils/appUtils";
import { prefetchCalendarAvailability } from "@app/availability/hooks";
import { useCheckoutSessionContext } from "@app/checkout/hooks";
import { FilterModalComponent } from "@app/common/components/FilterModalComponent";
import { prefetchExchangeRates } from "@app/common/queries";
import { useLogCheckoutItemPage } from "@app/common/services/activityLog/useLogCheckoutItemPage";
import { logLocation } from "@app/common/services/activityLog/utils";
import { IFilterGroup, TFilter } from "@app/common/types";
import { Flex } from "@app/componentLibrary/ui/flex";
import { LoadingDots } from "@app/componentLibrary/ui/LoadingDots";
import { ThemeTitle } from "@app/componentLibrary/ui/text";
import { prefetchProperty } from "@app/property/queries";
import { PropertyCard } from "@app/propertyGroup/components/PropertyCard";
import { useGetPropertyGroup } from "@app/propertyGroup/hooks";
import { useDebounce } from "@uidotdev/usehooks";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export function PropertySelectPage() {
    const navigate = useCustomNavigate();
    const { routerData } = useRouter();
    const { changeActivePropertyId, propertyGroupId, propertyGroup, activePropertyId } = usePropertyContext();
    const [hoveredPropertyId, setHoveredPropertyId] = useState<string>();

    const [filters, setFilters] = useState<TFilter>({});
    const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } = useGetPropertyGroup(propertyGroupId, {
        filters,
    });
    const preloadablePropertyId = useDebounce(hoveredPropertyId, 1250);

    const { t } = useTranslation(["navigation"]);
    const title = t("selectHotel", { ns: "navigation" });

    const { registerVisitedPage, globalState } = useAppState();
    const { state: checkoutSessionState, checkoutBookingValidation } = useCheckoutSessionContext();

    useLogCheckoutItemPage({
        page: "PropertySelectPage",
        visitorId: globalState.visitorId,
        checkoutSessionId: checkoutSessionState.session_id,
        bookingId: checkoutSessionState.booking_id,
        channelBookingId: checkoutBookingValidation?.data?.reservedBooking?.reservation_id,
        propertyId: activePropertyId,
        where: logLocation(2),
    });

    const prefetchPropertyData = useCallback(async (prefetchPropertyId: string) => {
        await prefetchProperty(prefetchPropertyId);
        await prefetchExchangeRates(prefetchPropertyId);
        await prefetchCalendarAvailability(prefetchPropertyId, new Date(), {
            staleTime: 1000 * 60 * 1, // 1 minute
        });
    }, []);

    useEffect(() => registerVisitedPage({ url: document.location.href, page: getRouteChildValue() || "" }), []);

    /**
     * Responsible to detect a change of preloadablePropertyId and prefetch data for the hovered property.
     */
    useEffect(() => {
        if (preloadablePropertyId) {
            prefetchPropertyData(preloadablePropertyId);
        }
    }, [preloadablePropertyId]);

    const handlePropertyClick = (selectedPropertyId: string) => {
        changeActivePropertyId(selectedPropertyId);

        /**
         * If somehow the selected property doesn't match the preloadable property,
         * eg when select and hover are withing the debounce timeout, make sure the
         * selected property is preloading
         */
        if (selectedPropertyId !== preloadablePropertyId) {
            prefetchPropertyData(selectedPropertyId);
        }

        navigate(routerData.next);
    };

    const handleScroll = (event: React.UIEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;
        const bottom = target.scrollHeight - target.scrollTop - 50 - target.clientHeight < 50;

        if (bottom && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    const handleMouseEnterPropertyCardContainer = async (propertyId: string) => {
        setHoveredPropertyId(propertyId);
    };

    /// ----------- filters ------------
    // fake filterdata including country and amenities
    const filterGroups = useMemo<IFilterGroup[]>(() => propertyGroup?.filter_groups || [], []);

    const handleFilterChange = useCallback((formFilters: { [key: string]: boolean | undefined }) => {
        setFilters(formFilters);
    }, []);

    const showFilterButton = useMemo(() => filterGroups.length > 0, [filterGroups]);

    return (
        <Fragment>
            <Flex
                onScroll={handleScroll}
                flexDirection={"col"}
                alignItems={"start"}
                justifyContent={"start"}
                data-testid="property-list"
                className="grow px-5 gap-y-8 overflow-auto h-auto pt-4 pb-8 w-full"
            >
                <ThemeTitle size="xxl">{title}</ThemeTitle>
                {showFilterButton && (
                    <FilterModalComponent filterGroups={filterGroups} defaultFilters={filters} onSubmit={handleFilterChange} />
                )}
                {data?.pages.map((pg, i) => (
                    <Fragment key={i}>
                        {pg.properties
                            .filter((item) => item.show_on_chain_page)
                            .map((property) => (
                                <div
                                    className="w-full"
                                    key={property.id}
                                    onMouseEnter={() => handleMouseEnterPropertyCardContainer(property.id)}
                                >
                                    <PropertyCard
                                        property={property}
                                        onClick={(selectedProperty) => handlePropertyClick(selectedProperty.id)}
                                    />
                                </div>
                            ))}
                    </Fragment>
                ))}
                {(isFetching || isFetchingNextPage) && (
                    <div className="self-center">
                        <LoadingDots />
                    </div>
                )}
            </Flex>
        </Fragment>
    );
}
